import { cssJoin } from '@volvo-cars/css/utils';
import type React from 'react';
import { forwardRef, useState } from 'react';
import type { GlobalHTMLAttributes } from './types';
import { useIds } from './use-ids';

type RangeStyle = React.CSSProperties & {
  '--range-value': number;
};

export interface RangeSliderProps extends GlobalHTMLAttributes {
  /**
   * The name of the input to use when submitting the form.
   */
  name?: string;

  /**
   * Id of a form element that this input should be associated with. Defaults to the containing
   * form element.
   */
  form?: string;

  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>;

  /**
   * The minimum value for the input.
   */
  min?: number;

  /**
   * The maximum value for the input.
   */
  max?: number;

  /**
   * The step interval between values.
   */
  step?: number;

  /**
   * Id of a `<datalist>` element with a list of predefined values to suggest to the user.
   */
  list?: string;

  /**
   * Default value of an uncontrolled input.
   */
  defaultValue?: number;

  /**
   * Provide a tooltip while sliding, indicating the current value.
   */
  tooltip?: boolean;

  /**
   * Value of the input.
   *
   * Makes the input controlled.
   */
  value?: number;

  /**
   * Fires immediately when the input’s value is changed by the user (for example, it fires on every keystroke).
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

/**
 * `RangeSlider` is shipped as a standalone React component without the standard label/hint/error configuration of most inputs,
 * as it's envisaged that its primary use will be in custom components. Due to the [fragmented browser landscape](https://github.com/w3c/csswg-drafts/issues/4410)
 * of native range inputs, we need to control aspects of the visual style with JavaScript - so we do not ship a HTML-only version of this component.
 * <hr/>
 * `RangeSlider` can be used uncontrolled (native), or controlled. To make a `RangeSlider` controlled, set both the `value` prop and the `onChange` handler.
 */
export const RangeSlider = forwardRef<HTMLInputElement, RangeSliderProps>(
  function RangeSlider(
    {
      min = 0,
      max = 100,
      value,
      defaultValue,
      onChange,
      tooltip = false,
      style,
      className,
      ...props
    },
    ref: React.ForwardedRef<HTMLInputElement>,
  ) {
    // Browsers set the thumb to half the range by default when value is not set. We need to make our filled track follow suit
    const initialValue = Number(value ?? defaultValue ?? max / 2);

    const [innerValue, setInnerValue] = useState(initialValue);

    const sliderValue = Math.max(
      min,
      Math.min(max, typeof value !== 'undefined' ? Number(value) : innerValue),
    );

    // Convert slider value to a proportion (0-1) used for track fill width and tooltip positioning
    const valueRatio = (sliderValue - min) / (max - min);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (typeof value === 'undefined') {
        setInnerValue(Number(event.target.value));
      }
      if (onChange) {
        onChange(event);
      }
    };

    const { rootId: sliderId } = useIds(props.id);
    const rangeStyle: RangeStyle = {
      ...style,
      '--range-value': valueRatio,
    };

    return tooltip ? (
      <div className={cssJoin('range', className)} style={rangeStyle}>
        <input
          {...props}
          id={sliderId}
          type="range"
          ref={ref}
          min={min}
          max={max}
          value={sliderValue}
          onChange={handleChange}
        />
        <output
          htmlFor={sliderId}
          slot="output"
          aria-live="polite"
          className="tooltip"
        >
          {sliderValue}
        </output>
      </div>
    ) : (
      <input
        {...props}
        type="range"
        className={cssJoin('range', className)}
        ref={ref}
        min={min}
        max={max}
        value={sliderValue}
        onChange={handleChange}
        style={rangeStyle}
      />
    );
  },
);
